// Shared floating music play/pause toggle. Replaces the per-variant emoji
// (🎵 / 🔇) buttons that violated the no-emoji-icons rule (font-dependent
// rendering, inconsistent across platforms). Inline SVG keeps the bundle
// dependency-free; icons are sized to inherit `currentColor` from the host
// button.
//
// Usage:
//   <MusicToggle on={music} onToggle={() => setMusic((v) => !v)} accent={t.accent} ink={t.ink} />

import type { CSSProperties } from "react";

export interface MusicToggleProps {
  /** Current playback state. */
  on: boolean;
  /** Called on click; parent flips the boolean and starts/stops playback. */
  onToggle: () => void;
  /** Background color (template's accent). */
  accent: string;
  /** Foreground / icon color. Defaults to white; pass the template's `bg` for dark variants. */
  ink?: string;
  /** Optional position override; defaults to bottom-right. */
  style?: CSSProperties;
  /** Optional className for variant-specific scoping (e.g. `v-w09-music`). */
  className?: string;
  /** ARIA label override. */
  label?: { play: string; pause: string };
}

const PlayIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polygon points="6 4 20 12 6 20 6 4" fill="currentColor" stroke="none" />
  </svg>
);

const PauseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="6" y="5" width="4" height="14" fill="currentColor" stroke="none" rx="1" />
    <rect x="14" y="5" width="4" height="14" fill="currentColor" stroke="none" rx="1" />
  </svg>
);

export function MusicToggle({
  on,
  onToggle,
  accent,
  ink = "#FFFFFF",
  style,
  className,
  label,
}: MusicToggleProps) {
  const aria = on
    ? label?.pause ?? "Musiqani o'chirish"
    : label?.play ?? "Musiqani yoqish";
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={aria}
      aria-pressed={on}
      className={className}
      style={{
        position: "fixed",
        bottom: 16,
        right: 16,
        zIndex: 40,
        width: 48,
        height: 48,
        minWidth: 44,
        minHeight: 44,
        borderRadius: "50%",
        background: accent,
        color: ink,
        border: "none",
        cursor: "pointer",
        boxShadow: "0 6px 18px -6px rgba(0,0,0,0.4)",
        display: "grid",
        placeItems: "center",
        ...style,
      }}
    >
      {on ? <PauseIcon /> : <PlayIcon />}
    </button>
  );
}
